(() => {
  'use strict';

  if (customElements.get('ttt-bundle-builder')) return;

  customElements.define(
    'ttt-bundle-builder',
    class TTTBundleBuilder extends HTMLElement {
      connectedCallback() {
        const payload = this.querySelector('[data-bb-products]');
        if (!payload) return;

        try {
          this.products = JSON.parse(payload.textContent);
          if (!Array.isArray(this.products)) return;
        } catch (_) {
          return; // malformed payload (e.g. Liquid error in the JSON): leave the section inert
        }
        this.productById = new Map(this.products.map((p) => [String(p.id), p]));
        this.variantIndex = new Map();
        this.products.forEach((p) =>
          p.variants.forEach((v) => this.variantIndex.set(String(v.id), { product: p, variant: v }))
        );

        this.tiers = Array.from(this.querySelectorAll('[data-bb-tier]'))
          .map((el) => ({
            el,
            quantity: Number(el.dataset.quantity),
            label: el.dataset.label,
            percent: Number(el.dataset.percent) || 0,
          }))
          .filter((t) => t.quantity > 0)
          .sort((a, b) => a.quantity - b.quantity);

        this.refs = {
          headline: this.querySelector('[data-bb-headline]'),
          count: this.querySelector('[data-bb-count]'),
          items: this.querySelector('[data-bb-items]'),
          empty: this.querySelector('[data-bb-empty]'),
          error: this.querySelector('[data-bb-error]'),
          atc: this.querySelector('[data-bb-atc]'),
          tracker: this.querySelector('[data-bb-tracker]'),
          dialog: this.querySelector('[data-bb-dialog]'),
          dialogContent: this.querySelector('[data-bb-dialog-content]'),
          dialogReviews: this.querySelector('[data-bb-dialog-reviews]'),
        };

        this.storageKey = `ttt-bb:${this.dataset.sectionId}`;
        this.bundle = this.loadBundle();
        this.cartLines = [];
        this.cartCount = 0;
        this.adding = false;
        this.changingCart = false;

        // Bind once: the theme editor re-runs connectedCallback on section
        // reorder with the same element instance, and listeners would stack.
        if (!this._bbBound) {
          this._bbBound = true;
          this.addEventListener('click', (e) => this.onClick(e));
          this.addEventListener('change', (e) => {
            const select = e.target.closest('[data-bb-variant-select]');
            if (select) this.onCardVariantChange(select);
          });
          this.refs.dialog.addEventListener('click', (e) => {
            if (e.target === this.refs.dialog) this.refs.dialog.close();
          });
        }

        // Click outside the open drawer closes it (mobile). Registered per
        // connect and removed on disconnect.
        this.onDocClick = (e) => {
          if (!this.refs.tracker.hasAttribute('data-drawer-open')) return;
          if (this.refs.tracker.contains(e.target)) return;
          this.closeDrawer();
        };
        document.addEventListener('click', this.onDocClick);

        // Keep the grid clearance in sync with the mobile bar's real height (CSS --bb-bar-height)
        this.trackerObserver = new ResizeObserver(() => {
          this.style.setProperty('--bb-bar-height', `${this.refs.tracker.offsetHeight}px`);
        });
        this.trackerObserver.observe(this.refs.tracker);

        // Dawn's constants.js/pubsub.js declare lexical globals (const/function),
        // so PUB_SUB_EVENTS is NOT a window property — reference them bare.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCartCount());
        }

        this.refreshCartCount();
        this.render();
      }

      disconnectedCallback() {
        if (this.unsubscribeCart) this.unsubscribeCart();
        if (this.trackerObserver) this.trackerObserver.disconnect();
        if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
      }

      /* --- Bundle state --- */

      loadBundle() {
        try {
          const raw = sessionStorage.getItem(this.storageKey);
          const parsed = raw ? JSON.parse(raw) : [];
          if (!Array.isArray(parsed)) return [];
          return parsed.filter((l) => this.variantIndex.has(String(l.variantId)) && l.qty > 0);
        } catch (_) {
          return [];
        }
      }

      saveBundle() {
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify(this.bundle));
        } catch (_) {
          /* storage unavailable: in-memory only */
        }
      }

      bundleCount() {
        return this.bundle.reduce((sum, l) => sum + l.qty, 0);
      }

      // Percent of the highest tier reached by the current total — purely
      // visual; the actual discount is applied by the discount app.
      currentDiscountPercent() {
        const count = this.totalCount();
        const reached = [...this.tiers].reverse().find((t) => count >= t.quantity);
        return reached ? reached.percent : 0;
      }

      totalCount() {
        return this.bundleCount() + this.cartCount;
      }

      addToBundle(variantId, qty) {
        const id = String(variantId);
        if (!this.variantIndex.has(id)) return;
        const line = this.bundle.find((l) => String(l.variantId) === id);
        if (line) line.qty += qty;
        else this.bundle.push({ variantId: id, qty });
        this.refs.error.hidden = true;
        this.saveBundle();
        this.render();
      }

      setQty(variantId, qty) {
        const id = String(variantId);
        if (qty > 0) {
          this.bundle = this.bundle.map((l) => (String(l.variantId) === id ? { ...l, qty } : l));
        } else {
          this.bundle = this.bundle.filter((l) => String(l.variantId) !== id);
        }
        this.refs.error.hidden = true;
        this.saveBundle();
        this.render();
      }

      /* --- Cart awareness --- */

      async refreshCartCount() {
        try {
          const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
          const cart = await fetch(`${cartUrl}.js`).then((r) => r.json());
          this.cartLines = (cart.items || [])
            .filter((item) => this.variantIndex.has(String(item.variant_id)))
            .map((item) => ({ key: item.key, variantId: String(item.variant_id), qty: item.quantity }));
          this.cartCount = this.cartLines.reduce((sum, l) => sum + l.qty, 0);
        } catch (_) {
          this.cartLines = [];
          this.cartCount = 0;
        }
        this.render();
      }

      async changeCartLine(key, qty) {
        if (this.changingCart) return;
        this.changingCart = true;
        try {
          const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
          const res = await fetch(`${changeUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: qty }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || data.status) {
            throw new Error(data.description || data.message || 'Unable to update the cart.');
          }
          this.refs.error.hidden = true;
          await this.refreshCartCount();
          // Let the rest of the theme react (header bubble etc.) — see pubsub note.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ttt-bundle-builder', cartData: data });
          }
        } catch (err) {
          this.refs.error.textContent = err.message;
          this.refs.error.hidden = false;
        } finally {
          this.changingCart = false;
          this.render();
        }
      }

      /* --- Rendering --- */

      formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        const format = this.dataset.moneyFormat || '${{amount}}';
        return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
      }

      popupPriceHtml(variant) {
        if (!variant) return '';
        const compare = variant.compare_at_price;
        if (compare && compare > variant.price) {
          return `<s class="ttt-bb__popup-compare">${this.formatMoney(compare)}</s> <span class="ttt-bb__popup-sale">${this.formatMoney(variant.price)}</span>`;
        }
        return this.formatMoney(variant.price);
      }

      // Reads the rating/count back off the pre-rendered Judge.me badge markup, so the
      // payload carries one field instead of three that could drift apart.
      badgeData(product) {
        if (!product || !product.badge) return null;
        const holder = document.createElement('div');
        holder.innerHTML = product.badge;
        const badge = holder.querySelector('.jdgm-prev-badge');
        if (!badge) return null;
        const reviews = Number(badge.getAttribute('data-number-of-reviews')) || 0;
        if (reviews <= 0) return null;
        return { rating: badge.getAttribute('data-average-rating'), reviews: reviews };
      }

      // Every popup is opened from a grid card, and those card badges are in the server
      // HTML so Judge.me has already hydrated them (it rewrites the bare "N reviews" from
      // the metafield into the store's "4.67 (N reviews)" format). Cloning the hydrated
      // card keeps the popup identical to the card and avoids re-rendering a badge that
      // Judge.me's script can no longer see, since the popup is built after docReady.
      syncPopupBadge() {
        const target = this.refs.dialogContent && this.refs.dialogContent.querySelector('.ttt-bb__popup-rating');
        if (!target || !this.popupProduct) return;
        const source = this.querySelector(`[data-bb-rating="${this.popupProduct.id}"]`);
        target.innerHTML = source ? source.innerHTML : this.popupProduct.badge || '';
      }

      render() {
        this.renderTracker();
        this.renderItems();
      }

      renderTracker() {
        const count = this.totalCount();
        const next = this.tiers.find((t) => count < t.quantity);
        const reached = [...this.tiers].reverse().find((t) => count >= t.quantity);

        if (next) {
          this.refs.headline.textContent = (this.dataset.msgProgress || '')
            .replace('{count}', next.quantity - count)
            .replace('{discount}', next.label);
        } else if (reached) {
          this.refs.headline.textContent = (this.dataset.msgMax || '').replace('{discount}', reached.label);
        }

        let prev = 0;
        this.tiers.forEach((t) => {
          const fill = t.el.querySelector('[data-bb-tier-fill]');
          const span = Math.max(1, t.quantity - prev);
          const progress = Math.min(1, Math.max(0, (count - prev) / span));
          fill.style.width = `${progress * 100}%`;
          t.el.classList.toggle('is-reached', count >= t.quantity);
          prev = t.quantity;
        });

        const inBundle = this.bundleCount();
        const total = this.totalCount();
        this.refs.count.textContent = total;
        this.refs.count.hidden = total === 0;
        this.refs.atc.disabled = inBundle === 0 || this.adding;
        this.refs.empty.hidden = total !== 0;
      }

      renderItems() {
        this.refs.items.innerHTML = '';
        this.cartLines.forEach((line) => this.renderLine(line, true));
        this.bundle.forEach((line) => this.renderLine(line, false));
      }

      renderLine(line, inCart) {
        const entry = this.variantIndex.get(String(line.variantId));
        if (!entry) return;
        const { product, variant } = entry;
        // Cart lines mutate via /cart/change.js keyed by line key; bundle lines by variant id.
        const act = inCart ? 'cart-' : '';
        const ref = inCart ? line.key : line.variantId;
        const flag = inCart ? ' <span class="ttt-bb__item-flag">In cart</span>' : '';
        const baseCents = variant.price * line.qty;
        const percent = this.currentDiscountPercent();
        const priceHtml =
          percent > 0
            ? `<s class="ttt-bb__item-compare">${this.formatMoney(baseCents)}</s> ${this.formatMoney(
                Math.round((baseCents * (100 - percent)) / 100)
              )}`
            : this.formatMoney(baseCents);
        const li = document.createElement('li');
        li.className = 'ttt-bb__item';
        li.innerHTML = `
          <img class="ttt-bb__item-image" src="${variant.image || product.image || ''}" alt="" width="56" height="56" loading="lazy">
          <div class="ttt-bb__item-info">
            <p class="ttt-bb__item-title"></p>
            <p class="ttt-bb__item-price">${priceHtml}${flag}</p>
            <div class="ttt-bb__item-qty">
              <button type="button" data-bb-${act}dec="${ref}" aria-label="Decrease quantity">&minus;</button>
              <span>${line.qty}</span>
              <button type="button" data-bb-${act}inc="${ref}" aria-label="Increase quantity">+</button>
              <button type="button" class="ttt-bb__item-remove" data-bb-${act}remove="${ref}">Remove</button>
            </div>
          </div>`;
        li.querySelector('.ttt-bb__item-title').textContent =
          variant.title === 'Default Title' ? product.title : `${product.title} — ${variant.title}`;
        this.refs.items.appendChild(li);
      }

      /* --- Events --- */

      onClick(e) {
        const hit = (sel) => e.target.closest(sel);
        let el;

        if ((el = hit('[data-bb-popup-add]'))) return this.popupAdd();
        if ((el = hit('[data-bb-popup-inc]'))) return this.popupSetQty(this.popupQty + 1);
        if ((el = hit('[data-bb-popup-dec]'))) return this.popupSetQty(this.popupQty - 1);
        if ((el = hit('[data-bb-open]'))) return this.openPopup(el.dataset.bbOpen);
        if ((el = hit('[data-bb-add]'))) return this.addToBundle(el.dataset.bbAdd, 1);
        if ((el = hit('[data-bb-card-add]'))) {
          // No fallback to a default variant: the card select starts on an
          // empty placeholder and the button stays disabled until the shopper
          // picks one, so a blank value here means nothing was chosen.
          const select = el.closest('.ttt-bb__card')?.querySelector('[data-bb-variant-select]');
          if (select && select.value) this.addToBundle(select.value, 1);
          return;
        }
        if ((el = hit('[data-bb-inc]'))) {
          const line = this.bundle.find((l) => String(l.variantId) === el.dataset.bbInc);
          if (line) this.setQty(el.dataset.bbInc, line.qty + 1);
          return;
        }
        if ((el = hit('[data-bb-dec]'))) {
          const line = this.bundle.find((l) => String(l.variantId) === el.dataset.bbDec);
          if (line) this.setQty(el.dataset.bbDec, line.qty - 1);
          return;
        }
        if ((el = hit('[data-bb-remove]'))) return this.setQty(el.dataset.bbRemove, 0);
        if ((el = hit('[data-bb-cart-inc]'))) {
          const line = this.cartLines.find((l) => l.key === el.dataset.bbCartInc);
          if (line) this.changeCartLine(line.key, line.qty + 1);
          return;
        }
        if ((el = hit('[data-bb-cart-dec]'))) {
          const line = this.cartLines.find((l) => l.key === el.dataset.bbCartDec);
          if (line) this.changeCartLine(line.key, line.qty - 1);
          return;
        }
        if ((el = hit('[data-bb-cart-remove]'))) return this.changeCartLine(el.dataset.bbCartRemove, 0);
        if (hit('[data-bb-drawer-close]')) return this.closeDrawer();
        if ((el = hit('[data-bb-review-toggle]'))) {
          const open = this.refs.tracker.toggleAttribute('data-drawer-open');
          el.setAttribute('aria-expanded', open);
          return;
        }
        if (hit('[data-bb-close]')) return this.refs.dialog.close();
        if (hit('[data-bb-atc]')) return this.addToCart();
      }

      closeDrawer() {
        this.refs.tracker.removeAttribute('data-drawer-open');
        const toggle = this.querySelector('[data-bb-review-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }

      /* --- Card variant options --- */

      // Selecting a variant updates the card's price and image and unlocks its
      // Add button. Nothing here needs to handle deselection: the placeholder
      // option is disabled, so it can't be chosen again.
      onCardVariantChange(select) {
        const card = select.closest('.ttt-bb__card');
        const entry = this.variantIndex.get(String(select.value));
        if (!card || !entry) return;
        const { variant } = entry;

        const priceEl = card.querySelector('[data-bb-card-price]');
        if (priceEl) {
          const compare = variant.compare_at_price;
          priceEl.innerHTML =
            compare && compare > variant.price
              ? `<s class="ttt-bb__card-compare">${this.formatMoney(compare)}</s> <span class="ttt-bb__card-sale">${this.formatMoney(variant.price)}</span>`
              : this.formatMoney(variant.price);
        }

        // variant.image already falls back to the product's featured image in
        // the payload, so a variant without its own shot keeps the product one.
        const imgEl = card.querySelector('[data-bb-card-image]');
        if (imgEl && variant.image) imgEl.src = variant.image;

        const addBtn = card.querySelector('[data-bb-card-add]');
        if (addBtn) addBtn.disabled = false;
      }

      /* --- Popup --- */

      openPopup(productId) {
        const product = this.productById.get(String(productId));
        if (!product) return;
        this.popupProduct = product;
        this.popupQty = 1;
        const firstAvailable = product.variants.find((v) => v.available) || product.variants[0];
        this.popupSelections = firstAvailable ? [...firstAvailable.options] : [];
        this.renderPopupReviews(product);
        this.renderPopup();
        this.refs.dialog.showModal();
      }

      currentPopupVariant() {
        if (!this.popupProduct) return null;
        // Matching assumes Shopify's invariant that variant.options is positionally
        // aligned with product.options_with_values (popupSelections is seeded from
        // variant.options, so the orders stay in lockstep).
        return (
          this.popupProduct.variants.find((v) =>
            v.options.every((val, i) => val === this.popupSelections[i])
          ) || null
        );
      }

      popupSetQty(qty) {
        this.popupQty = Math.max(1, qty);
        const qtyEl = this.refs.dialogContent.querySelector('[data-bb-popup-qty]');
        if (qtyEl) qtyEl.textContent = this.popupQty;
      }

      popupAdd() {
        const variant = this.currentPopupVariant();
        if (!variant || !variant.available) return;
        this.addToBundle(variant.id, this.popupQty);
        this.refs.dialog.close();
      }

      renderPopup() {
        const p = this.popupProduct;
        const variant = this.currentPopupVariant();
        const c = this.refs.dialogContent;
        const atcLabel = !variant ? 'Unavailable' : variant.available ? 'Add to Bundle' : 'Sold out';
        const atcDisabled = !variant || !variant.available ? 'disabled' : '';

        c.innerHTML = `
          <img class="ttt-bb__popup-image" src="${(variant && variant.image) || p.image || ''}" alt="">
          <h3 class="ttt-bb__popup-title"></h3>
          ${this.badgeData(p) ? `<div class="ttt-bb__popup-rating jdgm-widget jdgm-preview-badge" data-id="${p.id}" data-template="manual-installation"></div>` : ''}
          <p class="ttt-bb__popup-price">${this.popupPriceHtml(variant)}</p>
          <div class="ttt-bb__popup-options"></div>
          <div class="ttt-bb__popup-qty">
            <button type="button" data-bb-popup-dec aria-label="Decrease quantity">&minus;</button>
            <span data-bb-popup-qty>${this.popupQty}</span>
            <button type="button" data-bb-popup-inc aria-label="Increase quantity">+</button>
          </div>
          <button type="button" class="ttt-bb__btn ttt-bb__btn--card" data-bb-popup-add ${atcDisabled}>${atcLabel}</button>
          <div class="ttt-bb__popup-description"></div>`;

        c.querySelector('.ttt-bb__popup-title').textContent = p.title;
        c.querySelector('.ttt-bb__popup-description').innerHTML = p.description || '';

        if (p.variants.length > 1) {
          const wrap = c.querySelector('.ttt-bb__popup-options');
          p.options.forEach((opt, i) => {
            const label = document.createElement('label');
            label.className = 'ttt-bb__popup-option';
            const name = document.createElement('span');
            name.textContent = opt.name;
            const select = document.createElement('select');
            opt.values.forEach((val) => {
              const o = document.createElement('option');
              o.value = val;
              o.textContent = val;
              o.selected = val === this.popupSelections[i];
              select.appendChild(o);
            });
            select.addEventListener('change', () => {
              this.popupSelections[i] = select.value;
              this.renderPopup();
            });
            label.append(name, select);
            wrap.appendChild(label);
          });
        }

        this.syncPopupBadge();
      }

      renderPopupReviews(product) {
        const el = this.refs.dialogReviews;
        if (!el) return;
        const data = this.badgeData(product);
        if (!data) {
          el.innerHTML = '';
          return;
        }
        // Judge.me exposes no server-rendered review widget for this store (the
        // judgeme.widget metafield is empty), so the popup links out to the PDP anchor
        // instead of embedding the full list the way the Loox version tried to.
        el.innerHTML = `
          <details class="ttt-bb__reviews">
            <summary>Read reviews (${data.reviews})</summary>
            <a class="ttt-bb__reviews-link" href="${product.url}#judgeme_product_reviews">See all reviews on the product page</a>
          </details>`;
      }

      /* --- Add to cart --- */

      async addToCart() {
        if (!this.bundle.length || this.adding) return;
        this.adding = true;
        this.refs.atc.classList.add('is-loading');
        this.refs.error.hidden = true;

        const cartDrawer = document.querySelector('cart-drawer');
        const body = {
          items: this.bundle.map((l) => ({ id: Number(l.variantId), quantity: l.qty })),
        };
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }

        try {
          const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
          const res = await fetch(`${addUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json().catch(() => ({}));
          // Shopify cart errors can arrive as HTTP 200 with a `status` field
          // (see Dawn's product-form.js) — treat both shapes as failure. Non-JSON
          // bodies (proxy/HTML error pages) fall through to the generic message.
          if (!res.ok || data.status) {
            throw new Error(data.description || data.message || 'Unable to add bundle to cart.');
          }

          this.bundle = [];
          this.saveBundle();
          this.closeDrawer();

          // Bare lexical globals — see the pubsub note in connectedCallback.
          // Publishing alongside renderContents costs one redundant /cart.js fetch
          // (our own subscription re-counts), accepted so the tracker and any other
          // cartUpdate listeners stay authoritative.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'ttt-bundle-builder',
              cartData: data,
            });
          } else {
            this.refreshCartCount();
          }

          if (cartDrawer && data.sections) {
            cartDrawer.renderContents(data);
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        } catch (err) {
          this.refs.error.textContent = err.message;
          this.refs.error.hidden = false;
        } finally {
          this.adding = false;
          this.refs.atc.classList.remove('is-loading');
          this.render();
        }
      }
    }
  );
})();
